/**
 * Reports panel component for generating and viewing company reports
 */

import { useState } from 'react';
import { useReportTemplates, useGenerateReport } from '../../hooks/useCompanies';
import type { ReportTemplateInfo, GeneratedReport } from '../../types';
import { ReportViewer } from './ReportViewer';

interface ReportsPanelProps {
  companyId: string;
  companyName: string;
  isPublic: boolean;
}

const REPORT_ICONS: Record<string, string> = {
  executive_summary: '📊',
  financial_deep_dive: '📈',
  due_diligence: '🔍',
  competitive_intel: '🎯',
  weekly_digest: '📰',
  strategic_buyer: '🤝',
  market_landscape: '🌐',
};

interface GeneratedReportState {
  type: string;
  name: string;
  time: Date;
  report: GeneratedReport;
}

function ReportCard({
  template,
  isPublic,
  onGenerate,
  isGenerating,
  generatedReport,
  onView,
}: {
  template: ReportTemplateInfo;
  isPublic: boolean;
  onGenerate: () => void;
  isGenerating: boolean;
  generatedReport?: GeneratedReportState;
  onView: () => void;
}) {
  const isAvailable = !template.public_only || isPublic;
  const icon = REPORT_ICONS[template.type] || '📄';
  const hasGeneratedReport = generatedReport && generatedReport.type === template.type;

  return (
    <div
      className={`rounded-lg border p-4 ${
        isAvailable
          ? 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm transition-all'
          : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <h3 className="font-medium text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{template.description}</p>
          </div>
        </div>
        {template.public_only && (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            Public Only
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs text-gray-400">{template.section_count} sections</span>
        <div className="flex items-center gap-2">
          {hasGeneratedReport && (
            <button
              onClick={onView}
              className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              View
            </button>
          )}
          <button
            onClick={onGenerate}
            disabled={!isAvailable || isGenerating}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              isAvailable && !isGenerating
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </span>
            ) : hasGeneratedReport ? (
              'Regenerate'
            ) : (
              'Generate'
            )}
          </button>
        </div>
      </div>

      {hasGeneratedReport && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Generated {formatRelativeTime(generatedReport.time)}</span>
            {generatedReport.report.confidence_score !== undefined && (
              <span className={`font-medium ${
                generatedReport.report.confidence_score >= 0.7 ? 'text-green-600' :
                generatedReport.report.confidence_score >= 0.4 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {Math.round(generatedReport.report.confidence_score * 100)}% confidence
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export function ReportsPanel({ companyId, companyName, isPublic }: ReportsPanelProps) {
  const [generatingType, setGeneratingType] = useState<string | null>(null);
  const [generatedReports, setGeneratedReports] = useState<Map<string, GeneratedReportState>>(
    new Map()
  );
  const [viewingReport, setViewingReport] = useState<GeneratedReport | null>(null);

  const { data: templates, isLoading, error } = useReportTemplates();
  const generateMutation = useGenerateReport();

  const handleGenerate = async (template: ReportTemplateInfo) => {
    setGeneratingType(template.type);
    try {
      const report = await generateMutation.mutateAsync({
        companyId,
        reportType: template.type,
        title: `${template.name} - ${companyName}`,
      });

      // Store the generated report
      setGeneratedReports((prev) => {
        const next = new Map(prev);
        next.set(template.type, {
          type: template.type,
          name: template.name,
          time: new Date(),
          report: report as GeneratedReport,
        });
        return next;
      });

      // Auto-open the report viewer
      setViewingReport(report as GeneratedReport);
    } catch {
      // Report generation failed - error state handled by mutation
    } finally {
      setGeneratingType(null);
    }
  };

  const handleView = (reportType: string) => {
    const generated = generatedReports.get(reportType);
    if (generated) {
      setViewingReport(generated.report);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="animate-pulse">
                <div className="h-6 w-32 bg-gray-200 rounded mb-2" />
                <div className="h-4 w-48 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-700">Failed to load report templates.</p>
      </div>
    );
  }

  const lastGenerated = Array.from(generatedReports.values()).sort(
    (a, b) => b.time.getTime() - a.time.getTime()
  )[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
        {lastGenerated && (
          <button
            onClick={() => setViewingReport(lastGenerated.report)}
            className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 transition-colors"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>View {lastGenerated.name}</span>
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates?.map((template) => (
          <ReportCard
            key={template.type}
            template={template}
            isPublic={isPublic}
            onGenerate={() => handleGenerate(template)}
            isGenerating={generatingType === template.type}
            generatedReport={generatedReports.get(template.type)}
            onView={() => handleView(template.type)}
          />
        ))}
      </div>

      {!isPublic && (
        <p className="text-sm text-gray-500">
          Some reports are only available for public companies with SEC filings.
        </p>
      )}

      {generatedReports.size > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Generated Reports</h3>
          <div className="space-y-2">
            {Array.from(generatedReports.values())
              .sort((a, b) => b.time.getTime() - a.time.getTime())
              .map((gen) => (
                <div
                  key={gen.type}
                  className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{REPORT_ICONS[gen.type] || '📄'}</span>
                    <div>
                      <span className="text-sm font-medium text-gray-900">{gen.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {formatRelativeTime(gen.time)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setViewingReport(gen.report)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View
                  </button>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Report Viewer Modal */}
      {viewingReport && (
        <ReportViewer report={viewingReport} onClose={() => setViewingReport(null)} />
      )}
    </div>
  );
}

export default ReportsPanel;
